export const BROWSER_STORAGE_KEYS = {
  consent: "disciplio-browser-storage-consent-v1",
  workspace: "disciplio-api-workspace-v1",
  language: "disciplio-language-v1",
  goalCreationDrafts: "disciplio-goal-creation-drafts-v1",
  welcomeDraft: "disciplio-welcome-draft-v1",
  pendingVerification: "disciplio-pending-verification-v1",
  onboardingGuidePosition: "disciplio-onboarding-guide-position-v1",
  onboardingGuideCollapsed: "disciplio-onboarding-guide-collapsed-v1",
  notificationPromptDismissed: "disciplio-notification-prompt-dismissed-v1",
  shellAsideCollapsed: "disciplio-shell-aside-collapsed-v2",
  shellAsideWidth: "disciplio-shell-aside-width-v1",
  coachLastConversation: "disciplio-coach-last-conversation-v1",
} as const;

// v1.1: disclosure now also covers optional, privacy-friendly PostHog product
// analytics and session replay (see docs/analytics/POSTHOG.md).
export const BROWSER_STORAGE_CONSENT_VERSION = "cookies-v1.1";

export type BrowserStorageConsent = "accepted" | "rejected" | "undecided";

const optionalLocalStorageKeys = [
  BROWSER_STORAGE_KEYS.workspace,
  BROWSER_STORAGE_KEYS.language,
  BROWSER_STORAGE_KEYS.welcomeDraft,
  BROWSER_STORAGE_KEYS.pendingVerification,
  BROWSER_STORAGE_KEYS.onboardingGuidePosition,
  BROWSER_STORAGE_KEYS.onboardingGuideCollapsed,
  BROWSER_STORAGE_KEYS.notificationPromptDismissed,
  BROWSER_STORAGE_KEYS.shellAsideCollapsed,
  BROWSER_STORAGE_KEYS.shellAsideWidth,
  "disciplio-dark-right-nav-v2",
];

const optionalSessionStorageKeys = [
  BROWSER_STORAGE_KEYS.goalCreationDrafts,
  BROWSER_STORAGE_KEYS.coachLastConversation,
];

let inMemoryCoachLastConversationId: string | null = null;

function browserStorageAvailable() {
  return (
    typeof localStorage !== "undefined" && typeof sessionStorage !== "undefined"
  );
}

export function readBrowserStorageConsent(): BrowserStorageConsent {
  if (!browserStorageAvailable()) return "undecided";
  try {
    const raw = localStorage.getItem(BROWSER_STORAGE_KEYS.consent);
    if (!raw) return "undecided";
    const stored = JSON.parse(raw) as { version?: unknown; choice?: unknown };
    if (stored.version !== BROWSER_STORAGE_CONSENT_VERSION) return "undecided";
    return stored.choice === "accepted" || stored.choice === "rejected"
      ? stored.choice
      : "undecided";
  } catch {
    return "undecided";
  }
}

export function clearOptionalBrowserStorage() {
  inMemoryCoachLastConversationId = null;
  if (!browserStorageAvailable()) return;
  try {
    optionalLocalStorageKeys.forEach((key) => localStorage.removeItem(key));
    optionalSessionStorageKeys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Optional browser storage must never block the app if the browser denies access.
  }
}

/**
 * Keep the last Coach conversation available across SPA route changes without making an
 * unconsented browser-storage decision. Session storage is only used after the user accepts
 * optional storage, while the in-memory value covers the normal in-app navigation path.
 */
export function readCoachLastConversationId() {
  if (inMemoryCoachLastConversationId) return inMemoryCoachLastConversationId;
  if (readBrowserStorageConsent() !== "accepted" || typeof sessionStorage === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(BROWSER_STORAGE_KEYS.coachLastConversation);
    return stored?.trim() || null;
  } catch {
    return null;
  }
}

export function rememberCoachLastConversationId(conversationId: string | null) {
  inMemoryCoachLastConversationId = conversationId?.trim() || null;
  if (readBrowserStorageConsent() !== "accepted" || typeof sessionStorage === "undefined") return;
  try {
    if (inMemoryCoachLastConversationId) {
      sessionStorage.setItem(
        BROWSER_STORAGE_KEYS.coachLastConversation,
        inMemoryCoachLastConversationId,
      );
    } else {
      sessionStorage.removeItem(BROWSER_STORAGE_KEYS.coachLastConversation);
    }
  } catch {
    // Remembering the last Coach conversation must never block navigation.
  }
}

export function clearCoachLastConversationId() {
  inMemoryCoachLastConversationId = null;
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(BROWSER_STORAGE_KEYS.coachLastConversation);
  } catch {
    // Optional browser storage may be unavailable.
  }
}

export function saveBrowserStorageConsent(
  choice: Exclude<BrowserStorageConsent, "undecided">
) {
  if (choice === "rejected") clearOptionalBrowserStorage();
  if (!browserStorageAvailable()) return;
  try {
    localStorage.setItem(
      BROWSER_STORAGE_KEYS.consent,
      JSON.stringify({ version: BROWSER_STORAGE_CONSENT_VERSION, choice })
    );
  } catch {
    // The banner remains usable even when a browser blocks storage access.
  }
}
